/** Transport-independent view/attempt identities shared by both SPAs; delivery never controls product success. */
import { parseUiEvent, } from './ui-events.js';
export function createUiTracker(options) {
    let current;
    function emit(event) {
        try {
            options.send(parseUiEvent(event, options.pages));
        }
        catch {
            // Never log a rejected body or recursively report an analytics delivery error.
            console.error('UI_EVENT_DELIVERY_FAILED', { pageId: event.pageId, event: event.event });
        }
    }
    function enter(pageId, screenId, entryKey, journeyId) {
        const key = JSON.stringify([pageId, screenId, entryKey, journeyId]);
        if (current?.key === key)
            return current.view;
        const page = options.pages.find((candidate) => candidate.pageId === pageId);
        if (!page)
            throw new Error('UI_EVENT_UNKNOWN_PAGE');
        const view = {
            schemaVersion: 1,
            productSurface: options.productSurface,
            pageId,
            screenId,
            route: page.route,
            viewId: crypto.randomUUID(),
            ...(journeyId ? { journeyId } : {}),
        };
        current = { key, view };
        emit({ ...view, event: 'screen_viewed', eventId: crypto.randomUUID() });
        return view;
    }
    function request(actionId) {
        if (!current) {
            console.error('UI_EVENT_MISSING_VIEW');
            return undefined;
        }
        const page = options.pages.find((candidate) => candidate.pageId === current?.view.pageId);
        const completion = page?.actions[actionId];
        if (!completion) {
            console.error('UI_EVENT_UNKNOWN_ACTION', { pageId: current.view.pageId, actionId });
            return undefined;
        }
        const attempt = {
            view: current.view,
            actionId,
            completion,
            attemptId: crypto.randomUUID(),
            startedAt: Date.now(),
            settled: false,
        };
        emit({
            ...attempt.view,
            actionId,
            completion,
            attemptId: attempt.attemptId,
            event: 'action_requested',
            eventId: crypto.randomUUID(),
        });
        return attempt;
    }
    function finish(attempt, event, details = {}) {
        if (!attempt || attempt.settled)
            return;
        attempt.settled = true;
        emit({
            ...attempt.view,
            ...details,
            actionId: attempt.actionId,
            attemptId: attempt.attemptId,
            completion: attempt.completion,
            durationMs: Math.min(86_400_000, Math.max(0, Date.now() - attempt.startedAt)),
            event,
            eventId: crypto.randomUUID(),
        });
    }
    function blocked(actionId, reason) {
        if (!current)
            return console.error('UI_EVENT_MISSING_VIEW');
        const page = options.pages.find((candidate) => candidate.pageId === current?.view.pageId);
        const completion = page?.actions[actionId];
        if (!completion)
            return console.error('UI_EVENT_UNKNOWN_ACTION', { pageId: current.view.pageId, actionId });
        emit({
            ...current.view,
            actionId,
            completion,
            attemptId: crypto.randomUUID(),
            event: 'action_blocked',
            reason,
            eventId: crypto.randomUUID(),
        });
    }
    return {
        enter,
        request,
        blocked,
        completed: (attempt) => finish(attempt, 'action_completed'),
        failed: (attempt, details) => finish(attempt, 'action_failed', details),
        cancelled: (attempt, reason) => finish(attempt, 'action_cancelled', { reason }),
    };
}
//# sourceMappingURL=ui-tracker.js.map